import { joinSurveyAction } from '../join-survey';
import type { ConnectChannel } from '../types';

/**
 * Static content for the Attitudes channel detail page
 * (`/connect/attitudes`). Same sourcing rule as `demographics.ts`: copied,
 * not imported, since the source (Data Catalog / EXPLORE_CHANNELS) backs
 * internal-only tooling and this page is visible to every Home user.
 */
export const ATTITUDES_CHANNEL: ConnectChannel = {
    name: 'Attitudes',
    icon: 'brain',
    color: '#2E6649',
    coverage: 'US nationwide',
    freshness: 'Monthly',
    since: '2024',
    exploreDaniPrompt: 'What can Attitudes data tell me about my consumers?',
    actions: [
        {
            id: 'attitudes-profile',
            name: 'Profile opinions',
            icon: 'brain',
            summary:
                "what your target audience believes and values, compared with the rest of your category's buyers",
            template:
                'Profile the attitudes and values of {audience|my target audience} against all {category|category} buyers',
        },
        {
            id: 'attitudes-segments',
            name: 'Segment lifestyles',
            icon: 'users',
            summary: 'the lifestyle and values segments within your audience',
            template: 'Break {audience|my audience} into lifestyle and values segments',
        },
        {
            id: 'attitudes-motivations',
            name: 'Find motivations',
            icon: 'split',
            summary: 'what drives people to buy your brand and what holds others back',
            template:
                'What motivates buyers of {brand|my brand} in {category|my category}, and what holds back non-buyers?',
        },
        joinSurveyAction('attitudes'),
    ],
    schema: [
        { field: 'consumer_id', type: 'string', description: 'Pseudonymous panel ID' },
        { field: 'question_id', type: 'string', description: 'e.g. o1, o25, o71' },
        { field: 'question_text', type: 'string', description: 'Verbatim prompt' },
        { field: 'response', type: 'string', description: 'Single- or multi-select answer' },
        { field: 'response_at', type: 'timestamp', description: 'Time of response' },
        { field: 'theme', type: 'string', description: 'Topic cluster (economy, AI, media...)' },
    ],
    sampleCols: ['Question ID', 'Question', 'Unique consumers', 'Total responses'],
    sampleColWidths: ['70px', 'minmax(220px, 3fr)', 'minmax(90px, 1fr)', 'minmax(90px, 1fr)'],
    sampleRows: [
        ['o1', 'Concerned about the economy affecting your personal finances?', '2,000', '2,000'],
        [
            'o25',
            'Have you personally used an AI tool such as ChatGPT, Copilot, Gemini?',
            '2,000',
            '2,000',
        ],
        ['o71', 'Which social media apps have you used in the past 7 days?', '2,000', '7,853'],
        ['o70', 'Which grocery stores have you shopped at in the past month?', '2,000', '3,729'],
        ['o52', 'On a scale of 1-10, how happy are you with your life today?', '2,000', '2,000'],
        ['o41', 'Where do you get your news from?', '1,999', '4,388'],
    ],
};
